import {
    Badge,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
    Button,
    EmptyState,
    EmptyStateActions,
    EmptyStateContent,
    EmptyStateDescription,
    EmptyStateIcon,
    EmptyStateTitle,
    Switch,
    Text,
} from '@/ui/design-system/components';
import { useAlertConfigs, useUpdateAlertConfig } from 'utils/hooks/alerts';
import ExtLink from 'components/ExtLink';
import { FiPlus } from 'react-icons/fi';
import Link from 'components/Link';
import Loading from 'components/Loading';
import PopularitySingleIcon from 'components/PopularitySingleIcon';
import { SidebarTrigger } from '@/ui/design-system/components/ui/sidebar';
import { SlidersVertical } from 'lucide-react';
import { TikTokIcon } from 'components/Icons';
import { mixpanelTrack } from 'utils/app';
import toast from 'react-hot-toast';
import { useEffect } from 'react';

const DATA_SOURCE_DISPLAY_NAMES = {
    tiktok: { label: 'TikTok', icon: TikTokIcon },
    popularity: { label: 'Pop Index', icon: PopularitySingleIcon },
};

const normalizeCadenceForDataSource = (cadence, dataSource) => {
    if (dataSource === 'popularity') return 'monthly';
    return cadence;
};

function DataSourceBadge({ dataSource }) {
    if (!DATA_SOURCE_DISPLAY_NAMES[dataSource]) return null;
    const { icon: IconComponent, label } =
        DATA_SOURCE_DISPLAY_NAMES[dataSource];
    return (
        <Badge
            variant="secondary"
            className="bg-accent text-accent-foreground border-0 font-medium py-1.5 px-2"
        >
            {IconComponent ? <IconComponent className="h-3.5 w-3.5" /> : null}
            {label}
        </Badge>
    );
}

function AlertConfigItem({ config, onTogglePause }) {
    const country = config.country;
    const contextNames = Array.isArray(config.context_names)
        ? config.context_names.filter(Boolean)
        : [];
    return (
        <div className="bg-white border border-border rounded-lg p-4 flex flex-col gap-4">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <Switch
                        checked={!config.paused}
                        onCheckedChange={() => onTogglePause(config)}
                    />
                    <Text as="span" className="font-medium text-base">
                        {config.name}
                    </Text>
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    className="text-primary"
                    asChild
                >
                    <Link to={`/alerts/${config.alert_config_id}`}>
                        <SlidersVertical className="mr-1.5 h-4 w-4" />
                        Edit alert
                    </Link>
                </Button>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <DataSourceBadge dataSource={config.data_source} />
                {country ? (
                    <Badge variant="secondary" className="py-1.5 px-2">
                        <span className="uppercase">{country}</span>
                    </Badge>
                ) : null}
                {config.categories && config.categories.length > 0 ? (
                    <>
                        <Badge variant="secondary" className="py-1.5 px-2">
                            <span className="capitalize">
                                {config.categories[0].cat3 ||
                                    config.categories[0].cat2}
                            </span>
                        </Badge>
                        {config.categories.length > 1 && (
                            <Badge variant="secondary" className="py-1.5 px-2">
                                <span className="text-muted-foreground">
                                    +{config.categories.length - 1}
                                </span>
                            </Badge>
                        )}
                    </>
                ) : null}
            </div>

            <div className="flex items-center gap-3 text-xs leading-none">
                <span className="font-medium text-foreground whitespace-nowrap">
                    Contexts:
                </span>
                {contextNames.length > 0 ? (
                    <span className="text-muted-foreground truncate">
                        {contextNames.join(', ')}
                    </span>
                ) : (
                    <span className="text-muted-foreground italic">
                        No contexts selected.
                    </span>
                )}
            </div>
        </div>
    );
}

function AlertsHeader({ alertCount, children }) {
    return (
        <div>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
                <SidebarTrigger className="md:hidden" />
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink asChild>
                                <Link to="/alerts">Alerts</Link>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>All alerts</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
                <Button className="ml-auto" asChild>
                    <Link to="/alerts/new">
                        <FiPlus className="mr-2 h-4 w-4" />
                        Create new alert
                    </Link>
                </Button>
            </div>
            <div className="flex justify-between items-center py-3.5 px-5">
                <div className="flex items-center gap-2">
                    <Text
                        as="span"
                        size="md"
                        className="font-semibold lowercase first-letter:uppercase"
                    >
                        All alerts
                    </Text>
                    {(alertCount > 0 || alertCount === 0) && (
                        <Text as="span" size="sm" weight="medium" color="muted">
                            {alertCount}
                        </Text>
                    )}
                </div>
            </div>
            {children}
        </div>
    );
}

function Alerts() {
    const {
        data: configs,
        isLoading: configsLoading,
        status: configsStatus,
    } = useAlertConfigs();
    const updateConfig = useUpdateAlertConfig();

    useEffect(() => {
        mixpanelTrack('Alerts');
    }, []);

    const handleTogglePause = async (config) => {
        try {
            const newPausedState = !config.paused;
            await updateConfig.mutateAsync({
                alertConfigId: config.alert_config_id,
                data: {
                    brand_name: config.brand_name,
                    website_url: config.website_url,
                    categories: config.categories,
                    trend_type: config.trend_type,
                    cadence: normalizeCadenceForDataSource(
                        config.cadence,
                        config.data_source,
                    ),
                    country: config.country,
                    receiver_emails: config.receiver_emails,
                    name: config.name,
                    prompt: config.prompt,
                    data_source: config.data_source,
                    paused: newPausedState,
                    context_ids: config.context_ids,
                },
            });
            mixpanelTrack(newPausedState ? 'Pause Alert' : 'Unpause Alert', {
                alertConfigId: config.alert_config_id,
                brandName: config.brand_name,
                categories: config.categories
                    ?.map((cat) => cat.cat3 || cat.cat2)
                    .filter(Boolean),
                country: config.country,
                trendType: config.trend_type,
                dataSource: config.data_source,
            });
            toast.success(config.paused ? 'Alert unpaused' : 'Alert paused');
        } catch (error) {
            console.error('Failed to toggle alert pause:', error);
            toast.error('Failed to update alert status');
        }
    };

    if (configsLoading) {
        return (
            <AlertsHeader alertCount={null}>
                <div className="flex justify-center items-center h-32">
                    <Loading />
                </div>
            </AlertsHeader>
        );
    }

    const activeAlerts = configs?.alert_configs || [];
    const alertCount = configsStatus === 'success' ? activeAlerts.length : null;

    if (alertCount === 0) {
        return (
            <AlertsHeader alertCount={alertCount}>
                <div className="px-5">
                    <EmptyState>
                        <EmptyStateContent>
                            <EmptyStateIcon />
                            <div className="flex flex-col gap-2">
                                <EmptyStateTitle>No alerts yet</EmptyStateTitle>
                                <EmptyStateDescription>
                                    Create your first alert to get started.
                                </EmptyStateDescription>
                            </div>
                            <EmptyStateActions>
                                <Button asChild>
                                    <Link to="/alerts/new">
                                        <FiPlus className="mr-2 h-4 w-4" />
                                        Create new alert
                                    </Link>
                                </Button>
                                <Button variant="outline" asChild>
                                    <ExtLink
                                        href="https://help.spate.nyc/en/article/ai-alerts"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        css="text-decoration: none;"
                                    >
                                        Learn more
                                    </ExtLink>
                                </Button>
                            </EmptyStateActions>
                        </EmptyStateContent>
                    </EmptyState>
                </div>
            </AlertsHeader>
        );
    }

    return (
        <AlertsHeader alertCount={alertCount}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 px-5">
                {activeAlerts.map((config) => (
                    <AlertConfigItem
                        key={config.alert_config_id}
                        config={config}
                        onTogglePause={handleTogglePause}
                    />
                ))}
            </div>
        </AlertsHeader>
    );
}

export default Alerts;
